// Capacity / status predicates — single source of truth shared by the listing
// date-row button (components/trip.js TripButtonSelection), the GroupDetails
// "Book this trip" modal buttons (ProfileCard / ParticipantsList), and the
// reserve page (pages/reserve/Book.js). A trip whose paid slots have reached
// total_slots, or whose status is "waitlist", must NOT route a fresh booking to
// /reserve — the correct action is the waitlist/listing flow. Kept dependency-
// free so it's trivially unit-testable and the modal buttons and listing gate
// can't drift apart (which is what let a sold-out date reach the reserve page
// and dead-end on "Please select an activity/room").

export function isTripFull(trip) {
  return Number(trip?.num_slots_paid) >= Number(trip?.total_slots);
}

export function isTripWaitlistOnly(trip) {
  return isTripFull(trip) || trip?.status === "waitlist";
}

// True when a fresh booking may proceed to the reserve flow for this trip.
export function canRouteToReserve(trip) {
  return !isTripWaitlistOnly(trip);
}

// The deposit charged on confirmation for a Free Reserve booking — and the
// amount a normal (non-eligible) trip card asks the user to "Reserve with".
// Mirrors the BE deposit math: ceil(price × deposit_percentage / 100), with
// the same 20% default Book.js uses.
export function depositForTrip(price, depositPercentage) {
  const pct = depositPercentage ?? 20;
  return Math.ceil((Number(price || 0) * pct) / 100);
}

// Whether a trip's active discount permits Free Reserve. Free Reserve is built
// around early-bird pricing ("secure our best early bird prices for free"), and
// an early-bird tier-1 trip ALWAYS carries the computed `auto_early_bird`
// discount — so that discount must NOT disqualify it. Only a manual / general /
// group promo (a stacked, non-early-bird discount) should block Free Reserve.
// Accepts either a promo object (discountedTripIdToPromoObject[tripId]) or a
// { special_discount_percentage, special_discount_name } shape. Display-surface
// rule only (trip cards / project rows): checkout itself does not enforce it —
// Book.js gates Free Reserve on trip.free_reserve_allowed alone, and the BE
// bookTrip has no promo condition for FR.
export function promoAllowsFreeReserve(promo) {
  if (!(promo?.special_discount_percentage > 0)) return true;
  return promo?.special_discount_name === "auto_early_bird";
}
